import { Box, Grid, Paper } from "@mui/material";
import axios from "axios";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Global_Data } from "../../globalData/GlobalData";
import { serverAddress } from "../../config";
import PdfViewer from "./PDFViewer";
import ComponentLoadingBoundary from "../Loading/ComponentLoadingBoundary";
import usePendingRequests from "../Loading/usePendingRequests";

function CUSTOMBOX({ data, textValue }) {
  const globalData = Global_Data() || {};
  const { token } = globalData;

  const [htmlData, setHtmlData] = useState("");
  const htmlWrapperRef = useRef(null);
  const [htmlScale, setHtmlScale] = useState(1);
  const { loading, trackRequest } = usePendingRequests();

  const action = data?.data?.sAction?.toUpperCase();

  const mergedTextValue = useMemo(() => {
    return {
      ...(globalData?.textValue || {}),
      ...(textValue || {})
    };
  }, [globalData?.textValue, textValue]);

  const replacePlaceholders = (sourceUri, values) => {
    if (!sourceUri) return "";

    return sourceUri.replace(/{([^}]+)}/g, (match, key) => {
      const value = values?.[key];

      return value !== undefined && value !== null && value !== ""
        ? value
        : match;
    });
  };

  const uri = useMemo(() => {
    return replacePlaceholders(data?.data?.sDataSource, mergedTextValue);
  }, [data?.data?.sDataSource, mergedTextValue]);

  const isUriReady = Boolean(
    uri &&
      !uri.includes("{") &&
      !uri.includes("}") &&
      !uri.includes("[") &&
      !uri.includes("]")
  );

  useEffect(() => {
    if (action !== "HTML") return;
    if (!token || !isUriReady) return;

    trackRequest(() =>
      axios.get(`${serverAddress}${uri}`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        responseType: "text"
      })
    )
      .then(result => {
        setHtmlData(result?.data || "");
      })
      .catch(error => {
        console.error("[CUSTOMBOX] HTML fetch error:", error);
        setHtmlData("");
      });
  }, [action, uri, isUriReady, token]);

  useEffect(() => {
    if (action !== "HTML") return;

    const updateScale = () => {
      const wrapperWidth = htmlWrapperRef.current?.clientWidth || 0;
      const a4WidthPx = 794;

      if (wrapperWidth > 0) {
        setHtmlScale(Math.min(wrapperWidth / a4WidthPx, 1));
      }
    };

    updateScale();

    const observer = new ResizeObserver(updateScale);

    if (htmlWrapperRef.current) {
      observer.observe(htmlWrapperRef.current);
    }

    window.addEventListener("resize", updateScale);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateScale);
    };
  }, [action, htmlData]);

  return (
    <Grid item id={data?.component?.sName} {...data?.grid_props}>
      <ComponentLoadingBoundary
        loading={loading}
        label={`Loading ${data?.component?.sName}`}
        minHeight={120}
        size={28}
      >
        <Paper
          elevation={data?.component?.sProps?.elevation ?? 0}
          {...data?.component?.sProps}
          sx={{
            width: "100%",
            overflow: "auto",
            boxSizing: "border-box",
            ...(data?.component?.sProps?.sx || {})
          }}
        >
          {action === "HTML" && (
            <Box
              ref={htmlWrapperRef}
              sx={{
                width: "100%",
                overflow: "hidden",
                boxSizing: "border-box"
              }}
            >
              <Box
                sx={{
                  width: "794px",
                  transform: `scale(${htmlScale})`,
                  transformOrigin: "top left",
                  "& .page": {
                    margin: "0 auto"
                  },
                  "& img": {
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                    display: "block"
                  }
                }}
                dangerouslySetInnerHTML={{ __html: htmlData }}
              />
            </Box>
          )}

          {action === "PDF" && isUriReady && (
            <Box sx={{ width: "100%" }}>
              <PdfViewer uri={uri} />
            </Box>
          )}
        </Paper>
      </ComponentLoadingBoundary>
    </Grid>
  );
}

export default CUSTOMBOX;
